/**
 * @fileoverview The Add sheet of What to plant (E19, design.md 4 "The confirm sheet", mock-up e2b): a row's "+ Add" opens
 * it, and the add happens on its button, beside a Cancel (rule 7: Cancel on the left as the outline button, the add on
 * the right, filled and wider; ActionRow draws it, B111).
 *
 * The server says every word: "Add Blueberry?", what Planning means (one line for a crop with dates, another for one
 * with none here), the bed it goes to and whether it is her only or first bed, and the button's "Add Blueberry"; {plant}
 * is filled here with the row's name. The bed is the server's choice; she moves the plant from its page. A failed add
 * shows the server's refusal when it gave one meant for her, else the standard sentence (ServerFailureNote: reported,
 * the support address, no retry words).
 *
 * It opens solid: it slides up without the panel's usual fade, so the tab bar under it never shows through the buttons
 * while it rises (walk, 29 Oct).
 *
 * Where: WhatToPlantPage and WhatToPlantBrowsePage.
 * Words (en/es): sitewide.cancel (ActionRow's Cancel) and sitewide.saving (Button's loading word).
 *
 * @module AddCropSheet
 */

import React from 'react';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { serverMessage } from '@/shared/lib/errorMessage';
import type { WhatToPlantSheetWords } from '@/shared/api/whatToPlant';
import type { AddCropState } from './useAddCrop';
import { fill } from './fillWords';

/** What the sheet is handed. */
interface AddCropSheetProps {
  /** The add's state: the row asked about, the add and its failure. */
  adder: AddCropState;
  /** The server's words and bed. */
  words: WhatToPlantSheetWords;
}

/**
 * The sheet, open while a row is asked about.
 *
 * @returns The sheet.
 */
export const AddCropSheet: React.FC<AddCropSheetProps> = ({ adder, words }) => {
  const row = adder.asking;
  const plant = row?.name ?? '';
  const refusal = adder.error ? serverMessage(adder.error) : null;
  return (
    <BottomPanel
      open={row !== null}
      onOpenChange={(open) => { if (!open && !adder.adding) adder.cancel(); }}
      title={fill(words.heading, { plant })}
      wrapTitle
      // Solid from its first frame (walk, 29 Oct): the panel slides up without fading in, so the tab bar under it never
      // shows through its buttons while it opens. It sits above the bar (the dialog's layer) on its own background.
      className="data-[state=open]:fade-in-100"
    >
      <p className="text-sm text-muted-foreground">{row?.state === 'noDates' ? words.lineNoDates : words.lineDated}</p>
      {words.bed && (
        <div className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm">
          <span className="font-medium text-foreground">{words.bed.name}</span>
          <span className="text-muted-foreground">{words.bed.note}</span>
        </div>
      )}
      {adder.error ? (refusal
        ? <p role="alert" className="text-sm text-destructive">{refusal}</p>
        : <ServerFailureNote error={adder.error} where="whatToPlant.add" />) : null}
      <ActionRow onCancel={adder.cancel} disabled={adder.adding}>
        <Button type="button" disabled={adder.adding} loading={adder.adding} busy="adding" onClick={() => adder.confirm(words.bed?.id ?? null)}>
          {fill(words.add, { plant })}
        </Button>
      </ActionRow>
    </BottomPanel>
  );
};
